import { createElement } from 'react';

import { getDocument } from '@atlaskit/browser-apis';
import type { PortalProviderAPI } from '@atlaskit/editor-common/common';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { Decoration, type DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { MalleableUiPlugin, RemixDisabledReason } from '../malleableUiPluginType';
import { RemixButtonDecoration } from '../ui/components/RemixButtonDecoration';

const TYPE_REMIX_BUTTON = 'REMIX_BUTTON' as const;

export const findRemixButtonDecoration = (
	decorations: DecorationSet,
	from?: number,
	to?: number,
): Decoration[] => {
	return decorations.find(from, to, (spec) => spec.type === TYPE_REMIX_BUTTON);
};

type RemixButtonDecorationParams = {
	anchorName: string;
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	isDisabled?: boolean;
	isRovoLLMEnabled?: boolean;
	livePage?: boolean;
	nodeType: string;
	nodeViewPortalProviderAPI: PortalProviderAPI;
	remixDisabledReason?: RemixDisabledReason;
	rootAnchorName?: string;
	rootNodeType?: string;
	rootPos: number;
};

/** Right-edge Remix button: same gutter as left controls (side: -4) but positioned at block right edge. */
export const remixButtonDecoration = ({
	api,
	isDisabled,
	isRovoLLMEnabled,
	livePage,
	remixDisabledReason,
	rootPos,
	anchorName,
	nodeType,
	nodeViewPortalProviderAPI,
	rootAnchorName,
	rootNodeType,
}: RemixButtonDecorationParams): Decoration => {
	const key = crypto.randomUUID();
	const widgetSpec = {
		side: -4,
		type: TYPE_REMIX_BUTTON,
		destroy: (_: Node) => {
			nodeViewPortalProviderAPI.remove(key);
		},
	};

	return Decoration.widget(
		rootPos,
		(view, getPos) => {
			const doc = getDocument();
			if (!doc) {
				throw new Error('Document not available');
			}
			const element = doc.createElement('span');
			if (fg('confluence_remix_button_right_side_block_fg')) {
				// display:contents makes the span a "phantom" box - zero layout impact,
				// so it never pushes text down when inserted inline around media/embed wraps.
				// The inner position:absolute div in RemixButtonDecoration still renders correctly.
				element.style.display = 'contents';
			} else {
				element.style.display = 'block';
			}
			element.contentEditable = 'false';
			if (fg('confluence_remix_button_right_side_block_fg')) {
				// Generic right-edge control attribute for block-controls hover area (Remix-specific: data-blocks-remix-button-*)
				element.setAttribute('data-blocks-right-edge-button-container', 'true');
				element.setAttribute('data-blocks-right-edge-button-anchor', rootAnchorName ?? anchorName);
				element.setAttribute('data-blocks-remix-button-container', 'true');
				element.setAttribute('data-blocks-remix-button-anchor', rootAnchorName ?? anchorName);
			}
			element.setAttribute('data-testid', 'block-ctrl-remix-button-container');

			nodeViewPortalProviderAPI.render(
				() =>
					createElement(RemixButtonDecoration, {
						api,
						getPos,
						view,
						anchorName,
						rootAnchorName,
						rootNodeType: rootNodeType ?? nodeType,
						rootPos,
						livePage,
						isDisabled,
						isRovoLLMEnabled,
						remixDisabledReason,
					}),
				element,
				key,
				undefined,
				true,
			);

			return element;
		},
		widgetSpec,
	);
};
